import './payment-links.scss';

import { ReactComponent as ViewLinkIcon } from '@basqet-dashboard/common/assets/icons/view-link.svg';
import { Button, TableWrapper, WarningModal } from '@basqet-dashboard/common/components';
import { PERMISSIONS } from '@basqet-dashboard/common/config';
import { UserContext } from '@basqet-dashboard/common/context';
import { useCurrencies, useHasPermission } from '@basqet-dashboard/common/hooks';
import { useFetchSettlementAccounts } from '@basqet-dashboard/common/hooks/queries';
import { copyToClipboard, getCurrency } from '@basqet-dashboard/common/utils';
import { AddSettlementAccountModal, EmailVerificationModal } from '@components';
import { ANALYTICS_EVENTS } from '@config';
import { PAYMENT_LINKS_MODALS } from '@config/payment-links';
import { useAnalytics } from '@hooks';
import { useFetchPaymentLinks } from '@hooks/queries';
import { format, formatDistance } from 'date-fns';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { CreatePaymentLink } from './create-payment-link';
import { DeletePaymentLink } from './delete-payment-link';
import { NoPayments } from './no-payments';
import { QRCodeModal } from './qr-code-modal';

function PaymentLinks() {
  const { track } = useAnalytics();
  const { availableCurrencies } = useCurrencies();
  const { paymentLinks, refetch, isLoading, isRefetching } = useFetchPaymentLinks();

  const { userHasSettlementAccounts } = useFetchSettlementAccounts();
  const { userHasVerifiedEmail } = useContext(UserContext);

  const [currentModal, setCurrentModal] = useState('');
  const [linkToDelete, setLinkToDelete] = useState(null);
  const [qrCodeData, setQrCodeData] = useState({ url: '', title: '', paymentLinkId: '' });
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      track(ANALYTICS_EVENTS.PAYMENT_LINKS_PAGE_VIEWED);
      hasTrackedPageView.current = true;
    }
  }, []);

  let showIsFetching = (!paymentLinks.length && isLoading) || isRefetching || false;

  const openCreatePaymentModal = () => {
    track(ANALYTICS_EVENTS.ADD_PAYMENT_LINK_CLICKED);
    setCurrentModal(PAYMENT_LINKS_MODALS.CREATE);
  };
  const openDeletePaymentModal = () => setCurrentModal(PAYMENT_LINKS_MODALS.DELETE);
  const openQRCodeModal = (url, title = 'Payment Link QR Code', paymentLinkId = '') => {
    setQrCodeData({ url, title, paymentLinkId });
    setCurrentModal(PAYMENT_LINKS_MODALS.QR_CODE);
  };
  const closeModal = async () => {
    setCurrentModal('');
    refetch();
  };

  const openCreateSettlementModal = () => setCurrentModal('SETTLEMENT_ACCOUNT');

  const hasPermission = useHasPermission();
  const canManagePaymentLink = hasPermission(PERMISSIONS.PAYMENT_LINKS_MANAGE);

  const isCreatePaymentLinkModal = currentModal === PAYMENT_LINKS_MODALS.CREATE;
  const isDeletePaymentLinkModal = currentModal === PAYMENT_LINKS_MODALS.DELETE;
  const isSettlementModal = currentModal === 'SETTLEMENT_ACCOUNT';
  const isQRCodeModal = currentModal === PAYMENT_LINKS_MODALS.QR_CODE;

  const warnAboutDeletePaymentLink = async (slug) => {
    track(ANALYTICS_EVENTS.PAYMENT_LINK_DELETE_CLICKED, { payment_link_id: slug });
    setLinkToDelete(slug);
    openDeletePaymentModal();
  };

  const handleCopyLink = (paymentLinkId, paymentLinkUrl) => {
    copyToClipboard('Payment link', paymentLinkUrl).then(() => {
      track(ANALYTICS_EVENTS.PAYMENT_LINK_COPIED, { payment_link_id: paymentLinkId });
    });
  };

  const handleViewLink = (paymentLinkId) => {
    track(ANALYTICS_EVENTS.PAYMENT_LINK_VIEW_CLICKED, { payment_link_id: paymentLinkId });
  };

  const formattedData = paymentLinks.map((item) => {
    const paymentLinksBaseUrl = process.env.REACT_APP_PAYMENT_LINKS_BASE_URL;
    const { created_at, updated_at, currency: currency_id, amount, url_slug } = item;

    const currency = getCurrency(availableCurrencies, currency_id);

    const currency_amount = amount
      ? `${currency?.slug} ${amount}`
      : `Any Amount (${currency?.slug})`;

    const payment_link_url = paymentLinksBaseUrl + `/${url_slug}`;

    const created_on = formatDistance(new Date(created_at), new Date(), {
      addSuffix: true
    });

    const last_used = format(new Date(updated_at), 'dd - MM - yyyy');
    const view_link = (
      <a
        target="_blank"
        rel="noreferrer"
        href={payment_link_url}
        onClick={() => handleViewLink(url_slug)}>
        <ViewLinkIcon /> View Link
      </a>
    );

    const qr_code = (
      <Button
        size="table"
        variant="outline"
        onClick={() =>
          openQRCodeModal(payment_link_url, `QR Code - ${item.name || 'Payment Link'}`, url_slug)
        }>
        QR Code
      </Button>
    );

    const copy_or_delete_link = (
      <div className="link">
        <Button size="table" onClick={() => handleCopyLink(url_slug, payment_link_url)}>
          Copy Link
        </Button>
        {canManagePaymentLink && (
          <Button
            size="table"
            variant="danger"
            onClick={() => warnAboutDeletePaymentLink(url_slug)}>
            Delete Link
          </Button>
        )}
      </div>
    );

    return {
      ...item,
      currency_amount,
      created_on,
      updated_at,
      last_used,
      view_link,
      copy_or_delete_link,
      qr_code
    };
  });

  return (
    <div className="payment_links">
      <TableWrapper
        data={formattedData}
        title={(paymentLinks.length ? 'Current' : 'No') + ' Payment'}
        name="links"
        headerName="payment_links"
        hasFilters
        isFetching={showIsFetching}
        isLoading={isLoading}
        searchPlaceholder="Search for payment link"
        openModal={openCreatePaymentModal}
        emptyDataComponent={
          <NoPayments
            openModal={openCreatePaymentModal}
            canManagePaymentLink={canManagePaymentLink}
          />
        }
      />
      {isDeletePaymentLinkModal && (
        <DeletePaymentLink closeModal={closeModal} linkToDelete={linkToDelete} />
      )}
      {isCreatePaymentLinkModal && userHasSettlementAccounts && (
        <CreatePaymentLink
          closeModal={closeModal}
          onPaymentLinkCreated={(url, title, paymentLinkId) => {
            closeModal();
            openQRCodeModal(url, title, paymentLinkId);
          }}
        />
      )}

      {isCreatePaymentLinkModal && !userHasVerifiedEmail && (
        <EmailVerificationModal onModalClose={closeModal} />
      )}

      {isCreatePaymentLinkModal && userHasVerifiedEmail && !userHasSettlementAccounts && (
        <WarningModal
          header={{ name: 'No Settlement', cursive: 'Account' }}
          onModalClose={closeModal}>
          <p className="modal__text">Open a settlement account to create a payment link </p>
          <div>
            <Button isFull isLoading={isLoading} onClick={openCreateSettlementModal}>
              Create a Settlement Account
            </Button>{' '}
            <br />
            <Button variant="outline" isFull onClick={closeModal}>
              Cancel
            </Button>
          </div>
        </WarningModal>
      )}

      {isSettlementModal && <AddSettlementAccountModal onModalClose={closeModal} />}

      {isQRCodeModal && (
        <QRCodeModal
          paymentUrl={qrCodeData.url}
          title={qrCodeData.title}
          paymentLinkId={qrCodeData.paymentLinkId}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

export { PaymentLinks };
